import { useEffect } from 'react';
import useSWR from 'swr';

import config from '@rikstv/play-common/src/config';
import { Channel, Program } from '@rikstv/play-common/src/forces/channels/channel.types';
import { mapToChannel } from '@rikstv/play-common/src/forces/channels/mappers';
import { getRequestPathWithAccessToken } from '@rikstv/play-common/src/forces/utils/request';
import { useAuthToken } from '@rikstv/play-common/src/hooks/useAuthToken';
import { getStartEndTime } from '@rikstv/play-common/src/utils/date/date.utils';
import { captureExceptionInSentry } from '@rikstv/play-common/src/utils/errorTracker/tracking';

export const useLiveChannel = (
  channelId: string | undefined,
  now: Date = new Date()
): {
  channel: Channel | undefined;
  isLoading: boolean;
  upcomingPrograms: Program[];
  currentProgram: Program | undefined;
} => {
  const accessToken = useAuthToken();
  const isLoggedIn = !!accessToken;
  const channelUrl = channelId ? `/channel/${channelId}/details` : null;
  const {
    data: channel,
    error,
    isLoading,
  } = useSWR<Channel | undefined>(
    channelUrl,
    async path => {
      const channelResponse = await getRequestPathWithAccessToken<RiksTV.ClientAPI.Channel>(accessToken)(path, {
        url: config.contentSearchApiUrl,
      });
      const channel = mapToChannel(channelResponse, isLoggedIn);
      return channel;
    },
    {
      // refresh on focus change if more than 5 minutes on idle
      focusThrottleInterval: 300_000,
      // autorefresh every 30 min
      refreshInterval: 1_800_000,
    }
  );

  useEffect(() => {
    if (error) {
      captureExceptionInSentry(error);
    }
  }, [error]);

  const programs = channel?.programs || [];

  const upcomingPrograms = programs.filter(p => {
    const { start } = getStartEndTime(p.time, p.duration);
    return start > now;
  });

  const currentProgram = getCurrentProgram(channel, now);

  return { channel, isLoading, upcomingPrograms, currentProgram };
};

const getCurrentProgram = (channel: Channel | undefined, now: Date) => {
  return (channel?.programs || []).find(p => {
    const { start, end } = getStartEndTime(p.time, p.duration);
    return start < now && now < end;
  });
};
